import axios from 'axios'

const host = window.location.hostname
const port = window.location.port
// Si el frontend corre en el mismo puerto que el backend (producción), usar /api relativo
// Si corre en otro puerto (desarrollo con React dev server), apuntar a :5000
const isDev = port && port !== '5000' && port !== '80' && port !== '443'
const baseURL = isDev ? `http://${host}:5000/api` : '/api'

const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' }
})

api.interceptors.request.use(config => {
  // No sobreescribir si ya tiene Authorization (portal de clientes)
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('token')
    if (token) config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data)
}

export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getExpired: () => api.get('/clients/expired'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  remove: (id) => api.delete(`/clients/${id}`),
  registerPayment: (id, data) => api.post(`/clients/${id}/payment`, data)
}

export const paymentsAPI = {
  getAll: () => api.get('/payments'),
  getMonthly: (month, year) => api.get(`/payments/monthly?month=${month}&year=${year}`),
  create: (data) => api.post('/payments', data),
  remove: (id) => api.delete(`/payments/${id}`)
}

export const plansAPI = {
  getAll: () => api.get('/plans'),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  remove: (id) => api.delete(`/plans/${id}`)
}

export const classesAPI = {
  getAll: () => api.get('/classes'),
  getById: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post('/classes', data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  enroll: (id, clientId) => api.post(`/classes/${id}/enroll`, { clientId }),
  unenroll: (id, clientId) => api.post(`/classes/${id}/unenroll`, { clientId })
}

export const attendanceAPI = {
  getAll: (date) => api.get(`/attendance?date=${date || ''}`),
  checkIn: (data) => api.post('/attendance/checkin', data),
  getByClient: (id) => api.get(`/attendance/client/${id}`),
  getQR: (classId) => api.get(`/attendance/qr/${classId}`),
  getFixedQR: () => api.get('/attendance/qr-fixed'),
  qrCheckIn: (token, clientId) => api.post('/attendance/qr-checkin', { token, clientId }),
  autoCheckIn: (clientId) => api.post('/attendance/qr-auto-checkin', { clientId }),
  remove: (id) => api.delete(`/attendance/${id}`)
}

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats')
}

export const alertsAPI = {
  getAll: () => api.get('/alerts'),
  resolve: (id) => api.put(`/alerts/${id}/resolve`)
}

export const phrasesAPI = {
  getToday: () => api.get('/phrases/today'),
  getAll: () => api.get('/phrases'),
  create: (phrase, date) => api.post('/phrases', { phrase, date })
}

export const whatsappAPI = {
  getStatus: () => api.get('/whatsapp/status'),
  sendTest: (phone, message) => api.post('/whatsapp/test', { phone, message }),
  triggerCheck: () => api.post('/whatsapp/trigger-check'),
  restart: () => api.post('/whatsapp/restart'),
  logout: () => api.post('/whatsapp/logout'),
  connectPhone: (phoneNumber) => api.post('/whatsapp/connect-phone', { phoneNumber }),
  startQR: () => api.post('/whatsapp/start-qr'),
  getPauseStatus: () => api.get('/whatsapp/pause-status'),
  pause: (until) => api.post('/whatsapp/pause', { until }),
  resume: () => api.post('/whatsapp/resume'),
  getMorosas: () => api.get('/whatsapp/morosas'),
  sendMorosas: (clientIds) => api.post('/whatsapp/send-morosas', { clientIds })
}

export const portalAPI = {
  login: (name, lastName, code) => api.post('/portal/login', { name, lastName, code }),
  getProfile: (config) => api.get('/portal/profile', config),
  getPayments: (config) => api.get('/portal/payments', config),
  getAttendance: (config) => api.get('/portal/attendance', config),
  getClasses: (config) => api.get('/portal/classes', config),
  getMessage: (config) => api.get('/portal/message', config),
  getRoutines: (config) => api.get('/portal/routines', config),
  getGoals: (config) => api.get('/portal/goals', config),
  getProgress: (config) => api.get('/portal/progress', config),
  getMembership: (config) => api.get('/portal/membership', config),
  registerAttendance: (classId, config) => api.post('/portal/attendance', { classId }, config),
  enroll: (classId, config) => api.post(`/portal/classes/${classId}/enroll`, {}, config),
  unenroll: (classId, config) => api.post(`/portal/classes/${classId}/unenroll`, {}, config)
}

export const routinesAPI = {
  getAll: () => api.get('/routines'),
  getByClient: (clientId) => api.get(`/routines/client/${clientId}`),
  create: (data) => api.post('/routines', data),
  update: (id, data) => api.put(`/routines/${id}`, data),
  remove: (id) => api.delete(`/routines/${id}`)
}

export const goalsAPI = {
  getAll: () => api.get('/goals'),
  getByClient: (clientId) => api.get(`/goals/client/${clientId}`),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  remove: (id) => api.delete(`/goals/${id}`)
}

export const progressAPI = {
  getAll: () => api.get('/progress'),
  getByClient: (clientId) => api.get(`/progress/client/${clientId}`),
  create: (data) => api.post('/progress', data),
  remove: (id) => api.delete(`/progress/${id}`)
}

export const messagesAPI = {
  getAll: () => api.get('/messages'),
  getLatest: () => api.get('/messages/latest'),
  create: (text) => api.post('/messages', { text }),
  update: (id, data) => api.put(`/messages/${id}`, data),
  remove: (id) => api.delete(`/messages/${id}`)
}

export const wellbeingAPI = {
  getMoodsToday: () => api.get('/wellbeing/moods/today'),
  getHabits: () => api.get('/wellbeing/habits'),
  createHabit: (data) => api.post('/wellbeing/habits', data),
  updateHabit: (id, data) => api.put(`/wellbeing/habits/${id}`, data),
  removeHabit: (id) => api.delete(`/wellbeing/habits/${id}`),
  getTodayStats: () => api.get('/wellbeing/habits/today-stats'),
  getMoodToday: (config) => api.get('/wellbeing/mood/today', config),
  postMood: (data, config) => api.post('/wellbeing/mood', data, config),
  getHabitsToday: (config) => api.get('/wellbeing/habits/today', config),
  toggleHabit: (habitId, config) => api.post('/wellbeing/habits/toggle', { habitId }, config),
  getSummary: (config) => api.get('/wellbeing/summary', config)
}

export default api
